import { type CountReasonData } from "~/gql/graphqlBase.generated";

export interface ReasonSelection {
	code: number;
	text: string;
}

export type StickyReasonAction =
	| "availability.add"
	| "availability.adjust"
	| "availability.count"
	| "availability.move"
	| "availability.regrade"
	| "availability.scrap"
	| "availability.transfer"
	| "fulfill.add"
	| "fulfill.adjust"
	| "fulfill.csvImport"
	| "fulfill.grade"
	| "fulfill.itemsTable"
	| "fulfill.move"
	| "fulfill.reconcile"
	| "fulfill.scrap"
	| "fulfill.transfer"
	| "inventory.gridEdit"
	| "material.count"
	| "material.move"
	| "material.scrap"
	| "produce.hardgoods.add"
	| "produce.hardgoods.adjust";

type CountReasonOption = Pick<CountReasonData, "code" | "reason">;

export interface ResolveStickyReasonInput {
	fallbackCode?: number;
	reasons: readonly CountReasonOption[] | undefined;
	stored: ReasonSelection | null;
}

export const STICKY_REASON_KEY_PREFIX = "stickyReason.";

export const stickyReasonStorageKey = (action: string): string => `${STICKY_REASON_KEY_PREFIX}${action}`;

const isReasonSelection = (value: unknown): value is ReasonSelection =>
	typeof value === "object" &&
	value !== null &&
	typeof (value as ReasonSelection).code === "number" &&
	typeof (value as ReasonSelection).text === "string";

const hasReasonList = (reasons: readonly CountReasonOption[] | undefined): reasons is readonly CountReasonOption[] =>
	(reasons?.length ?? 0) > 0;

const listedReason = (reasons: readonly CountReasonOption[], code: number): ReasonSelection | null => {
	const match = reasons.find((reason) => reason.code === code);
	return match ? { code: match.code, text: match.reason } : null;
};

const read = (action: StickyReasonAction): ReasonSelection | null => {
	try {
		const raw = localStorage.getItem(stickyReasonStorageKey(action));
		if (!raw) return null;
		const parsed = JSON.parse(raw) as unknown;
		return isReasonSelection(parsed) ? { code: parsed.code, text: parsed.text } : null;
	} catch {
		return null;
	}
};

const write = (action: StickyReasonAction, reason: ReasonSelection): void => {
	try {
		localStorage.setItem(stickyReasonStorageKey(action), JSON.stringify({ code: reason.code, text: reason.text }));
	} catch {
		// QuotaExceededError — storage full, keep the in-memory reason
	}
};

const clear = (action: StickyReasonAction): void => {
	try {
		localStorage.removeItem(stickyReasonStorageKey(action));
	} catch {
		// localStorage can be unavailable in private mode
	}
};

const isStale = (stored: ReasonSelection | null, reasons: readonly CountReasonOption[] | undefined): boolean =>
	stored !== null && hasReasonList(reasons) && listedReason(reasons, stored.code) === null;

const resolveDefault = ({ fallbackCode, reasons, stored }: ResolveStickyReasonInput): ReasonSelection | null => {
	if (!hasReasonList(reasons)) return stored;
	const storedListed = stored ? listedReason(reasons, stored.code) : null;
	if (storedListed) return storedListed;
	return fallbackCode === undefined ? null : listedReason(reasons, fallbackCode);
};

const pickerOptions = (
	reasons: readonly CountReasonOption[] | undefined,
	selected: ReasonSelection | null
): ReasonSelection[] => {
	if (hasReasonList(reasons)) return reasons.map((reason) => ({ code: reason.code, text: reason.reason }));
	return selected ? [selected] : [];
};

const stickyReason = { clear, isStale, pickerOptions, read, resolveDefault, write };

export default stickyReason;
